import type { App, DirectiveBinding } from 'vue'
import { watchEffect, type WatchStopHandle } from 'vue'
import { useUserStore } from '@/stores/user'

// 扩展 HTMLElement 类型，用于存储 stopWatcher
interface HTMLElementWithPermission extends HTMLElement {
  __permissionStopWatcher?: WatchStopHandle
}

type PermissionValue =
  | string
  | string[]
  | { whiteList: true }
  | {
      roles?: string | string[]
      whiteList?: boolean
      canAccess?: boolean
      canAccessVideo?: boolean
    }

const permission = {
  mounted(el: HTMLElement, binding: DirectiveBinding<PermissionValue>) {
    const userStore = useUserStore()
    const element = el as HTMLElementWithPermission

    // 使用 watchEffect 响应式监听 userStore 的变化
    const stopWatcher = watchEffect(() => {
      // 访问响应式数据，建立依赖关系
      const userRoles = userStore.getSysRoles()
      const userUid = userStore.getUid()
      const whiteListUids = userStore.uids
      const userInfo = userStore.userInfo
      const canAccessRadio = userStore.canAccessRadio
      const canAccessVideo = userStore.canAccessVideo

      // 当这些值变化时，重新检查权限
      // 这里访问这些值只是为了建立响应式依赖，实际检查在 checkPermission 中
      void userRoles
      void userUid
      void whiteListUids
      void userInfo
      void canAccessRadio
      void canAccessVideo

      // 执行权限检查
      checkPermission(el, binding)
    })

    // 将 stopWatcher 存储到元素上，以便在 unmounted 时清理
    element.__permissionStopWatcher = stopWatcher
  },
  updated(el: HTMLElement, binding: DirectiveBinding<PermissionValue>) {
    // updated 时也检查权限（当绑定值变化时）
    checkPermission(el, binding)
  },
  unmounted(el: HTMLElement) {
    // 清理 watchEffect
    const element = el as HTMLElementWithPermission
    const stopWatcher = element.__permissionStopWatcher
    if (stopWatcher) {
      stopWatcher()
      delete element.__permissionStopWatcher
    }
  },
}

/**
 * 获取有效的用户角色列表
 * ROLE_CREATOR 是最低权限，所有用户默认拥有
 */
function getEffectiveUserRoles(userRoles: string[]): string[] {
  const effectiveRoles = [...userRoles]
  // 如果用户角色列表中没有 ROLE_CREATOR，则默认添加（最低权限）
  if (!effectiveRoles.includes('ROLE_CREATOR')) {
    effectiveRoles.push('ROLE_CREATOR')
  }
  return effectiveRoles
}

/**
 * 编程式检查权限值，返回是否有权限
 */
export function checkPermissionValue(value: PermissionValue | undefined | null): boolean {
  try {
    const userStore = useUserStore()

    if (!value) {
      return false
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
      const config = value as {
        whiteList?: boolean
        roles?: string | string[]
        canAccess?: boolean
        canAccessVideo?: boolean
      }
      const userRoles = userStore.getSysRoles()
      const effectiveUserRoles = getEffectiveUserRoles(userRoles)
      const userUid = userStore.getUid()
      const whiteListUids = userStore.uids

      let hasRolePermission = false
      if (config.roles) {
        const requiredRoles = Array.isArray(config.roles) ? config.roles : [config.roles]
        hasRolePermission = requiredRoles.some((role) => effectiveUserRoles.includes(role))
      }

      let hasWhiteListPermission = false
      if (config.whiteList === true) {
        hasWhiteListPermission = whiteListUids.includes(userUid)
      }

      let hasCanAccessPermission = false
      if (config.canAccess === true) {
        hasCanAccessPermission = userStore.canAccessRadio
      }

      let hasCanAccessVideoPermission = false
      if (config.canAccessVideo === true) {
        hasCanAccessVideoPermission = userStore.canAccessVideo
      }

      if (
        config.roles !== undefined ||
        config.whiteList !== undefined ||
        config.canAccess !== undefined ||
        config.canAccessVideo !== undefined
      ) {
        return (
          hasRolePermission ||
          hasWhiteListPermission ||
          hasCanAccessPermission ||
          hasCanAccessVideoPermission
        )
      } else {
        return false
      }
    } else if (typeof value === 'string' || Array.isArray(value)) {
      const userRoles = userStore.getSysRoles()
      const effectiveUserRoles = getEffectiveUserRoles(userRoles)
      const requiredRoles = Array.isArray(value) ? value : [value]
      return requiredRoles.some((role) => effectiveUserRoles.includes(role))
    }

    return false
  } catch (error) {
    console.warn('[checkPermissionValue] 权限检查失败:', error)
    return false
  }
}

/**
 * 检查权限（用于指令）
 */
function checkPermission(el: HTMLElement, binding: DirectiveBinding<PermissionValue>) {
  const hasPermission = checkPermissionValue(binding.value)
  el.style.display = hasPermission ? '' : 'none'
}

/**
 * 注册权限指令
 */
export function setupPermissionDirective(app: App) {
  app.directive('permission', permission)
}

export default permission
